import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../services/api';

const money = (n) => `£${Number(n).toFixed(2)}`;

const SERVICES = [
  { key: 'laundry', title: '🧺 Laundry', meta: 'Wash, dry & fold' },
  { key: 'shirts_trousers', title: '👔 Shirts & Trousers', meta: 'Washed & pressed' }
];

const BookSubscription = () => {
  const [pricing, setPricing] = useState(null);
  const [service, setService] = useState('laundry');
  const [frequency, setFrequency] = useState('weekly');
  const [tier, setTier] = useState(1);
  const [pickupDate, setPickupDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    subscriptionsAPI.getPricing().then((r) => setPricing(r.data)).catch(() => setError('Failed to load plans'));
  }, []);

  const minDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const tiers = pricing?.[service]?.[frequency] || {};
  const current = tiers?.[tier];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!pickupDate) return setError('Please choose your first collection day.');
    setLoading(true);
    try {
      await subscriptionsAPI.create({ subscriptionType: service, tier: parseInt(tier, 10), frequency, pickupDate });
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  if (!pricing) return <div className="loading">Loading plans…</div>;

  return (
    <div className="container">
      <h1>Start a subscription</h1>
      <p className="section-sub" style={{ textAlign: 'left', margin: '0.3rem 0 0' }}>
        Regular collections, one simple price. Pause or cancel anytime.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="booking-layout">
          <div>
            {/* Service */}
            <div className="card" style={{ marginBottom: '1.2rem' }}>
              <h3 style={{ marginBottom: '0.8rem' }}>1. Choose your service</h3>
              <div className="choice-grid">
                {SERVICES.map((s) => (
                  <button type="button" key={s.key} className={`choice ${service === s.key ? 'selected' : ''}`} onClick={() => setService(s.key)}>
                    <div className="choice-title">{s.title}</div>
                    <div className="choice-meta">{s.meta}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div className="card" style={{ marginBottom: '1.2rem' }}>
              <h3 style={{ marginBottom: '0.8rem' }}>2. How often?</h3>
              <div className="choice-grid">
                {['weekly', 'fortnightly'].map((f) => (
                  <button type="button" key={f} className={`choice ${frequency === f ? 'selected' : ''}`} onClick={() => setFrequency(f)}>
                    <div className="choice-title" style={{ textTransform: 'capitalize' }}>{f}</div>
                    <div className="choice-meta">{f === 'weekly' ? 'Every week' : 'Every 2 weeks'}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tier */}
            <div className="card">
              <h3 style={{ marginBottom: '0.8rem' }}>3. Pick a plan size</h3>
              <div className="choice-grid">
                {[1, 2, 3].map((t) => {
                  const p = tiers?.[t];
                  if (!p) return null;
                  return (
                    <button type="button" key={t} className={`choice ${Number(tier) === t ? 'selected' : ''}`} onClick={() => setTier(t)}>
                      <div className="choice-title">Tier {t}</div>
                      <div className="choice-meta">{p.description}</div>
                      <div className="price-small" style={{ marginTop: '0.4rem' }}>{money(p.price)}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Summary */}
          <aside className="booking-summary">
            <div className="card">
              <h3>Your plan</h3>
              <div style={{ marginTop: '0.8rem' }}>
                <div className="summary-line"><span>Service</span><span>{SERVICES.find((s) => s.key === service)?.title.replace(/^\S+\s/, '')}</span></div>
                <div className="summary-line"><span>Plan</span><span>{current?.description}</span></div>
                <div className="summary-line" style={{ textTransform: 'capitalize' }}><span>Frequency</span><span>{frequency}</span></div>
                <div className="summary-total">
                  <span>{money(current?.price || 0)}</span>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink-soft)' }}>/{frequency === 'weekly' ? 'week' : 'fortnight'}</span>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>First collection day</label>
                <input type="date" value={pickupDate} min={minDate()} onChange={(e) => setPickupDate(e.target.value)} required />
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">You're all set! Redirecting…</div>}

              <button type="submit" className="btn btn-accent btn-block" disabled={loading}>
                {loading ? 'Setting up…' : 'Subscribe'}
              </button>
              <p style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', textAlign: 'center', marginTop: '0.7rem' }}>
                Cancel anytime from your dashboard.
              </p>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
};

export default BookSubscription;
